import { useEffect, useState } from 'react'
import { useProjectPlanSnapshotRepository } from '../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { ProgressReportScopeType } from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  fetchProjectPlanSelectRows,
  fetchTicketListSelectRows,
} from '../lib/snapshotRows'
import { resolveSnapshotWbsItemType } from '../lib/snapshotWbsItemType'

type UseRootNodeDisplayNameProps = {
  snapshotUuid: string | undefined
  scopeType: ProgressReportScopeType
  rootUuid: string | undefined
}

type RootNodeInfo = {
  displayName: string | undefined
  iconUrl: string | undefined
}

const EMPTY_ROOT_NODE_INFO: RootNodeInfo = {
  displayName: undefined,
  iconUrl: undefined,
}

export const useRootNodeDisplayName = ({
  snapshotUuid,
  scopeType,
  rootUuid,
}: UseRootNodeDisplayNameProps): RootNodeInfo => {
  const { fetchMasterSchedule, fetchMasterScheduleTicketLists } =
    useProjectPlanSnapshotRepository()
  const { wbsItemTypes, ticketTypes } = useProjectPrivateContext()
  const [rootNodeInfo, setRootNodeInfo] =
    useState<RootNodeInfo>(EMPTY_ROOT_NODE_INFO)

  useEffect(() => {
    // 取得が終わるまで前ページの集計範囲が残ると、表示中の数値の集計範囲を誤認させるためクリアする
    setRootNodeInfo(EMPTY_ROOT_NODE_INFO)
    if (!snapshotUuid || !rootUuid) return

    const cancelled = { current: false }
    const handleError = (error: unknown) => {
      console.error('Failed to fetch root node display name:', error)
    }

    if (scopeType === 'TICKET_LIST') {
      fetchTicketListSelectRows(snapshotUuid, fetchMasterScheduleTicketLists)
        .then(rows => {
          if (cancelled.current) return
          const row = rows.find(row => row.uuid === rootUuid)
          setRootNodeInfo({
            displayName: row?.displayName,
            iconUrl: ticketTypes.find(type => type.code === row?.ticketType)
              ?.iconUrl,
          })
        })
        .catch(handleError)
    } else {
      fetchProjectPlanSelectRows(snapshotUuid, fetchMasterSchedule)
        .then(rows => {
          if (cancelled.current) return
          const row = rows.find(row => row.uuid === rootUuid)
          setRootNodeInfo({
            displayName: row?.displayName,
            iconUrl: row
              ? resolveSnapshotWbsItemType(
                  row.treeValue,
                  row.type,
                  wbsItemTypes
                )?.iconUrl
              : undefined,
          })
        })
        .catch(handleError)
    }

    return () => {
      cancelled.current = true
    }
  }, [
    snapshotUuid,
    scopeType,
    rootUuid,
    fetchMasterSchedule,
    fetchMasterScheduleTicketLists,
    wbsItemTypes,
    ticketTypes,
  ])

  return rootNodeInfo
}
